import { DATE_TIME_FORMATS } from "constant";
import moment, { Moment } from "moment";

export const isSystem12HourFormat = () => {
    try {
        // Test with 1 PM (13:00) to detect format
        const testDate = new Date(2023, 0, 1, 13, 0, 0);
        const formatter = new Intl.DateTimeFormat(navigator.language, {
            hour: "numeric",
            hour12: undefined, // Let system decide
        });

        const formattedTime = formatter.format(testDate);

        // Extract the hour number from formatted string
        const hourMatch = formattedTime.match(/(\d+)/);
        const displayedHour = hourMatch ? parseInt(hourMatch[1]) : 0;

        // Simple logic: if 13:00 displays as "13", it's 24-hour format
        // if 13:00 displays as anything else (1, etc.), it's 12-hour format
        const is12Hour = displayedHour !== 13;

        return is12Hour;
    } catch (error) {
        // If anything fails, default to 12-hour format for better UX
        console.warn(
            "Error detecting time format, defaulting to 12-hour format:",
            error
        );
        return true;
    }
};

export const getDateTimeFormat = (includeSeconds = false) => {
    const is12Hour = isSystem12HourFormat();
    const { LONG_DT_12H, LONG_DT_24H, SHORT_DT_12H, SHORT_DT_24H } =
        DATE_TIME_FORMATS;

    if (includeSeconds) {
        return is12Hour ? LONG_DT_12H : LONG_DT_24H;
    }

    return is12Hour ? SHORT_DT_12H : SHORT_DT_24H;
};

export const getTimeFormat = (includeSeconds = false) => {
    const is12Hour = isSystem12HourFormat();
    const { LONG_TIME_12H, LONG_TIME_24H, SHORT_TIME_12H, SHORT_TIME_24H } =
        DATE_TIME_FORMATS;

    if (includeSeconds) {
        return is12Hour ? LONG_TIME_12H : LONG_TIME_24H;
    }

    return is12Hour ? SHORT_TIME_12H : SHORT_TIME_24H;
};

export const getInputTimeFormat = () => {
    const is12Hour = isSystem12HourFormat();
    const { INPUT_SHORT_TIME_12H, INPUT_SHORT_TIME_24H } = DATE_TIME_FORMATS;

    return is12Hour ? INPUT_SHORT_TIME_12H : INPUT_SHORT_TIME_24H;
};

const getFormattedTimeOfDay = (timeOfDay = "start", includeSeconds = false) => {
    let timeObj;

    if (timeOfDay === "end") {
        // For end of day, we use 23:59:59
        timeObj = moment().endOf("day");
    } else {
        // For start of day, we use 00:00:00
        timeObj = moment().startOf("day");
    }

    return timeObj.format(getTimeFormat(includeSeconds));
};

export const getStartTimeOfDay = (includeSeconds = false) => {
    return getFormattedTimeOfDay("start", includeSeconds);
};

export const getEndTimeOfDay = (includeSeconds = false) => {
    return getFormattedTimeOfDay("end", includeSeconds);
};

export const getCalendarFormat = () => {
    const timeFormat = getTimeFormat();
    const dtFormat = getDateTimeFormat();
    const calendarFormat = {
        sameDay: `[Today at] ${timeFormat}`,
        nextDay: `[Tomorrow at] ${timeFormat}`,
        nextWeek: `dddd [at] ${timeFormat}`,
        lastDay: `[Yesterday at] ${timeFormat}`,
        lastWeek: `[Last] dddd [at] ${timeFormat}`,
        sameElse: dtFormat,
    };

    return calendarFormat;
};

export const getValidDateTimeFormats = (is12Hour: boolean): string[] => {
    if (is12Hour) {
        return [
            "DD/MM/YYYY hh:mm A", // 27/05/2025 03:33 PM
            "DD/MM/YYYY h:mm A", // 27/05/2025 3:33 PM
        ];
    } else {
        return [
            "DD/MM/YYYY HH:mm", // 27/05/2025 15:33
            "DD/MM/YYYY H:mm", // 27/05/2025 3:33
        ];
    }
};

/*
 * Assume input July 2025, it will return a list of weekdays index and a list of weekends index of July 2026.
 * Weekday example: [0, 1, 2, 3, 6, 7, 8, 9, 10, 13, 14, 15, 16, 17, 20, 21, 22, 23, 24, 27, 28, 29, 30]
 * Value 0 is the index of the date July 1, 2025, which is Tuesday.
 * Value 6 is the index of the date July 7, 2025, which is Monday.
 * Why index: it is used in the array after retrieving the consumption chart data
 * @param month The input month
 * @returns array of weekdays indexes
 */
export const getWeekdaysWeekendsIndexInMonth = (month: moment.Moment) => {
    const numberDays = month.daysInMonth();
    const weekdayIndexes = [];
    const weekendIndexes = [];

    const currentDay = moment(month);

    for (let i = 1; i <= numberDays; i++) {
        const newDay = currentDay.date(i);

        // check if this day is a weekday, 0 is Sunday, 6 is Saturday
        if (newDay.day() > 0 && newDay.day() < 6) {
            weekdayIndexes.push(i - 1);
        } else {
            weekendIndexes.push(i - 1);
        }
    }

    return { weekdayIndexes, weekendIndexes };
};

// Check if two dates are the same time unit
// @param date1 The first date
// @param date2 The second date
// @param timeUnit The time unit to check
// @returns True if the dates are the same time unit, false otherwise
export const isSameTimeUnit = (date1: Moment, date2: Moment, timeUnit: moment.unitOfTime.StartOf): boolean => {
    switch (timeUnit) {
        case "day":
            return (
                date1.date() === date2.date() &&
                date1.month() === date2.month() &&
                date1.year() === date2.year()
            );
        case "week":
            return (
                date1.week() === date2.week() &&
                date1.year() === date2.year()
            );
        case "month":
            return (
                date1.month() === date2.month() &&
                date1.year() === date2.year()
            );
        case "year":
            return date1.year() === date2.year();
        default:
            return date1.isSame(date2, timeUnit);
    }
};